<template>
  <div class="precall">
    <!-- ═══ TOP BAR ═══ -->
    <div class="precall-top">
      <span class="vm-mark">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </span>
      <span class="ttl">Sala de espera</span>
      <span class="clock">{{ clockDisplay }}</span>
    </div>

    <!-- ═══ BODY ═══ -->
    <div class="precall-body">
      <!-- CÂMERA PREVIEW -->
      <div class="cam-preview">
        <video
          v-show="mediaStream && !videoMuted"
          ref="preview"
          autoplay
          muted
          playsinline
          class="cam-video"
        />
        <div
          v-show="!mediaStream || videoMuted"
          class="stack"
          style="align-items:center;gap:var(--space-3)"
        >
          <div
            class="avatar avatar--xl"
            style="background:rgba(255,255,255,.1);color:#fff"
          >
            {{ userInitials }}
          </div>
          <span
            style="color:rgba(255,255,255,.55);font-size:var(--font-size-sm)"
          >
            {{ mediaError || "Câmera desativada" }}
          </span>
        </div>
        <div class="selfname-row">
          <span class="selfname-txt">{{ currentUserName }} (você)</span>
        </div>
        <div class="cam-controls">
          <div class="mic-wrapper" @click="toggleAudio">
            <span
              v-if="!audioMuted && mediaStream"
              class="sound-ring ring-1"
              :style="soundRingStyle(1)"
            />
            <span
              v-if="!audioMuted && mediaStream"
              class="sound-ring ring-2"
              :style="soundRingStyle(2)"
            />
            <span
              v-if="!audioMuted && mediaStream"
              class="sound-ring ring-3"
              :style="soundRingStyle(3)"
            />
            <span
              class="cam-ctrl"
              :class="{ off: audioMuted }"
              title="Microfone"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"
                  stroke="#fff"
                  stroke-width="2"
                />
                <path
                  d="M5 11a7 7 0 0 0 14 0M12 18v3"
                  stroke="#fff"
                  stroke-width="2"
                  stroke-linecap="round"
                />
                <line
                  v-if="audioMuted"
                  x1="3"
                  y1="3"
                  x2="21"
                  y2="21"
                  stroke="#fff"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </span>
          </div>
          <span
            class="cam-ctrl"
            :class="{ off: videoMuted }"
            title="Câmera"
            @click="toggleVideo"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="2"
                y="6"
                width="14"
                height="12"
                rx="2"
                stroke="#fff"
                stroke-width="2"
              />
              <path
                d="M16 10l6-3v10l-6-3"
                stroke="#fff"
                stroke-width="2"
                stroke-linejoin="round"
              />
              <line
                v-if="videoMuted"
                x1="3"
                y1="3"
                x2="21"
                y2="21"
                stroke="#fff"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </span>
          <span
            class="cam-ctrl"
            title="Configurações"
            @click="showSettings = !showSettings"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="3" stroke="#fff"
stroke-width="2"
/>
              <path
                d="M19 12a7 7 0 0 0-.1-1l2-1.6-2-3.4-2.4 1a7 7 0 0 0-1.7-1l-.4-2.5h-4l-.4 2.5a7 7 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.6a7 7 0 0 0 0 2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 1.7 1l.4 2.5h4l.4-2.5a7 7 0 0 0 1.7-1l2.4 1 2-3.4-2-1.6a7 7 0 0 0 .1-1z"
                stroke="#fff"
                stroke-width="2"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </div>

        <!-- PAINEL DE CONFIGURAÇÕES -->
        <div v-if="showSettings" class="settings-panel">
          <p class="settings-title">Dispositivos</p>
          <label class="settings-label">Câmera</label>
          <select
            class="settings-select"
            v-model="selectedVideoDevice"
            @change="restartPreview"
          >
            <option
              v-for="d in videoDevices"
              :key="d.deviceId"
              :value="d.deviceId"
            >
              {{ d.label || "Câmera " + (videoDevices.indexOf(d) + 1) }}
            </option>
          </select>
          <label class="settings-label">Microfone</label>
          <select
            class="settings-select"
            v-model="selectedAudioDevice"
            @change="restartPreview"
          >
            <option
              v-for="d in audioDevices"
              :key="d.deviceId"
              :value="d.deviceId"
            >
              {{ d.label || "Microfone " + (audioDevices.indexOf(d) + 1) }}
            </option>
          </select>
        </div>
      </div>

      <!-- PAINEL DE PRONTIDÃO -->
      <div class="ready-panel">
        <div>
          <h2>Tudo pronto para entrar</h2>
          <p v-if="!isDoctor">
            O profissional de saúde entrará no horário marcado. Você pode entrar
            e aguardar na sala.
          </p>
          <p v-else>
            O paciente entrará no horário marcado. Você pode entrar e aguardar
            na sala.
          </p>
        </div>

        <div class="ready-doc">
          <img
            v-if="otherPersonPhoto"
            :src="otherPersonPhoto"
            :alt="doctorName"
            class="avatar avatar--md avatar--ring"
            style="object-fit:cover"
          />
          <div v-else class="avatar avatar--md avatar--ring">
            {{ doctorInitials }}
          </div>
          <div class="stack" style="gap:1px;flex:1">
            <span style="color:#fff;font-weight:var(--fw-bold)">
              {{ doctorName }}
            </span>
            <span
              v-if="doctorSpecialty || doctorCRM"
              style="color:rgba(255,255,255,.6);font-size:var(--font-size-xs)"
            >
              {{ [doctorSpecialty, doctorCRM].filter(Boolean).join(" · ") }}
            </span>
          </div>
          <span v-if="!entryLocked" class="badge badge--warning">
            Conectando…
          </span>
        </div>

        <div class="ready-checks">
          <div class="it">
            <span class="ck" :class="{ 'ck--fail': !cameraOk }">
              <svg
                v-if="cameraOk"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <svg
                v-else
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                />
              </svg>
            </span>
            {{ cameraOk ? "Câmera funcionando" : "Câmera indisponível" }}
          </div>
          <div class="it">
            <span class="ck" :class="{ 'ck--fail': !micOk }">
              <svg
                v-if="micOk"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <svg
                v-else
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                />
              </svg>
            </span>
            {{ micOk ? "Microfone funcionando" : "Microfone indisponível" }}
          </div>
          <div class="it">
            <span class="ck" :class="{ 'ck--fail': !connectionOk }">
              <svg
                v-if="connectionOk"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <svg
                v-else
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                />
              </svg>
            </span>
            {{ connectionOk ? "Conexão estável" : "Sem conexão" }}
          </div>
        </div>

        <div
          v-if="scheduledAt"
          class="lockbox"
          :class="{ 'lockbox--unlocked': !entryLocked }"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="4"
              y="10"
              width="16"
              height="11"
              rx="2"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              v-if="entryLocked"
              d="M8 10V7a4 4 0 0 1 8 0v3"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              v-else
              d="M8 10V7a4 4 0 0 1 8 0"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <div class="stack" style="gap:1px">
            <span class="lock-strong">
              {{
                entryLocked
                  ? `A consulta começa às ${scheduledAtLabel}`
                  : "Sala liberada"
              }}
            </span>
            <span class="lock-weak">
              {{
                entryLocked
                  ? `O botão libera automaticamente às ${unlockAtLabel} (${entryLockMinutes} min antes).`
                  : `Aberta às ${unlockAtLabel}.`
              }}
            </span>
          </div>
        </div>

        <button
          v-if="isDoctor"
          class="btn btn--outline btn--block copy-link-btn"
          @click="copyRoomLink"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="9"
              y="9"
              width="13"
              height="13"
              rx="2"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
              stroke="currentColor"
              stroke-width="2"
            />
          </svg>
          {{ linkCopied ? "Link copiado!" : "Copiar link da sala" }}
        </button>

        <button
          class="btn btn--accent btn--lg btn--block"
          :disabled="entryLocked"
          @click="enterRoom"
        >
          Entrar na consulta
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { isDoctor } from "../utils/userRole";
import { ENTRY_LOCK_MINUTES, unlockAt, isLocked } from "../utils/roomEntryLock";

export default {
  name: "WaitingRoomView",

  data() {
    return {
      audioMuted: false,
      videoMuted: false,
      currentTime: "",
      nowTs: Date.now(),
      timeInterval: null,
      mediaStream: null,
      mediaError: null,
      showSettings: false,
      videoDevices: [],
      audioDevices: [],
      selectedVideoDevice: null,
      selectedAudioDevice: null,
      audioLevel: 0,
      connectionOnline: navigator.onLine,
      roomData: null,
      linkCopied: false
    };
  },

  async mounted() {
    document.body.classList.remove("doctor-app-body");
    this.updateClock();
    this.timeInterval = setInterval(this.updateClock, 1000);
    window.addEventListener("online", this.updateConnectionStatus);
    window.addEventListener("offline", this.updateConnectionStatus);
    this.getRoomDetailsV2(this.$route.params.room)
      .then(response => {
        this.roomData = response.data;
      })
      .catch(() => {});
    await this.startPreview();
  },

  beforeDestroy() {
    clearInterval(this.timeInterval);
    window.removeEventListener("online", this.updateConnectionStatus);
    window.removeEventListener("offline", this.updateConnectionStatus);
    this.stopPreview();
  },

  methods: {
    ...mapActions("room", ["getRoomDetailsV2"]),

    updateConnectionStatus() {
      this.connectionOnline = navigator.onLine;
    },

    updateClock() {
      this.nowTs = Date.now();
      this.currentTime = new Date(this.nowTs).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    async startPreview() {
      this.mediaError = null;
      try {
        const videoConstraint = this.selectedVideoDevice
          ? { deviceId: { exact: this.selectedVideoDevice } }
          : true;
        const audioConstraint = this.selectedAudioDevice
          ? { deviceId: { exact: this.selectedAudioDevice } }
          : true;

        try {
          this.mediaStream = await navigator.mediaDevices.getUserMedia({
            video: videoConstraint,
            audio: audioConstraint
          });
        } catch (err) {
          const name = err && err.name;
          if (name === "AbortError" || name === "NotReadableError") {
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
              video: false,
              audio: audioConstraint
            });
            this.mediaError = "Câmera indisponível — entrando apenas com áudio";
          } else {
            throw err;
          }
        }

        this.startAudioAnalysis();
        this.$nextTick(() => {
          if (this.$refs.preview)
            this.$refs.preview.srcObject = this.mediaStream;
        });
      } catch (err) {
        const name = err && err.name;
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          this.mediaError =
            "Permissão negada — verifique as configurações do navegador";
        } else {
          this.mediaError = "Sem acesso à câmera ou microfone";
        }
      } finally {
        await this.loadDevices();
      }
    },

    stopPreview() {
      this.stopAudioAnalysis();
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(t => t.stop());
        this.mediaStream = null;
      }
    },

    async restartPreview() {
      this.stopPreview();
      await this.startPreview();
    },

    startAudioAnalysis() {
      if (!this.mediaStream) return;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      this._audioCtx = new AudioCtx();
      this._analyser = this._audioCtx.createAnalyser();
      this._analyser.fftSize = 256;
      this._audioCtx
        .createMediaStreamSource(this.mediaStream)
        .connect(this._analyser);
      this._dataArray = new Uint8Array(this._analyser.frequencyBinCount);
      const loop = () => {
        if (!this._analyser) return;
        this._analyser.getByteFrequencyData(this._dataArray);
        const avg =
          this._dataArray.reduce((a, b) => a + b, 0) / this._dataArray.length;
        this.audioLevel = Math.min(100, (avg / 128) * 220);
        this._animFrame = requestAnimationFrame(loop);
      };
      loop();
    },

    stopAudioAnalysis() {
      if (this._animFrame) cancelAnimationFrame(this._animFrame);
      if (this._audioCtx) this._audioCtx.close().catch(() => {});
      this._audioCtx = null;
      this._analyser = null;
      this.audioLevel = 0;
    },

    soundRingStyle(ring) {
      const base = 1 + (this.audioLevel / 100) * 0.55 * ring;
      const opacity = this.audioMuted
        ? 0
        : Math.max(0, (this.audioLevel / 100) * (1 - (ring - 1) * 0.28));
      return {
        transform: `scale(${base})`,
        opacity
      };
    },

    async loadDevices() {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.videoDevices = devices.filter(d => d.kind === "videoinput");
      this.audioDevices = devices.filter(d => d.kind === "audioinput");
    },

    toggleAudio() {
      this.audioMuted = !this.audioMuted;
      if (this.mediaStream) {
        this.mediaStream.getAudioTracks().forEach(t => {
          t.enabled = !this.audioMuted;
        });
      }
    },

    toggleVideo() {
      this.videoMuted = !this.videoMuted;
      if (this.mediaStream) {
        this.mediaStream.getVideoTracks().forEach(t => {
          t.enabled = !this.videoMuted;
        });
      }
    },

    copyRoomLink() {
      const url = `${window.location.origin}/sala-de-espera/${this.$route.params.room}`;
      navigator.clipboard.writeText(url).then(() => {
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2500);
      });
    },

    enterRoom() {
      this.stopPreview();
      const roomId = this.$route.params.room;
      this.$router.push({
        name: "MeetingRoomView",
        params: {
          room: roomId,
          audioMuted: this.audioMuted,
          videoMuted: this.videoMuted
        }
      });
    }
  },

  computed: {
    ...mapGetters("user", ["getCurrentUser"]),
    ...mapGetters("appointments", ["currentSchedule"]),

    clockDisplay() {
      if (this.minutesLeft === null) return this.currentTime;
      if (this.minutesLeft === 0) return `${this.currentTime} · em andamento`;
      return `${this.currentTime} · faltam ${this.minutesLeft} min`;
    },

    cameraOk() {
      return !!this.mediaStream && !this.mediaError;
    },

    micOk() {
      return !!this.mediaStream && !this.mediaError;
    },

    connectionOk() {
      return this.connectionOnline;
    },

    userInitials() {
      return (
        (this.getCurrentUser?.name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || ""
      );
    },

    currentUserName() {
      return (
        (this.getCurrentUser?.name || "")
          .split(" ")
          .slice(0, 2)
          .join(" ") || ""
      );
    },

    isDoctor() {
      return isDoctor(this.$store.state.user.currentUser);
    },

    doctor() {
      if (this.isDoctor) {
        return this.roomData?.patient || {};
      }
      return this.roomData?.doctor || this.currentSchedule?.doctor || {};
    },

    otherPersonPhoto() {
      if (this.isDoctor) {
        return this.roomData?.patient?.photo || null;
      }
      return this.roomData?.doctor?.photo || null;
    },

    doctorInitials() {
      return (
        (this.doctor.name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || ""
      );
    },

    doctorName() {
      return this.doctor.name || "";
    },

    doctorSpecialty() {
      return this.doctor.doctor_property?.medical_specialty_name || "";
    },

    doctorCRM() {
      return this.doctor.doctor_property?.council_description || "";
    },

    minutesLeft() {
      const dt = this.scheduledAt;
      if (!dt) return null;
      return Math.max(0, Math.floor((new Date(dt) - this.nowTs) / 60000));
    },

    scheduledAt() {
      return this.roomData?.scheduled_at || this.currentSchedule?.scheduled_at;
    },

    entryLocked() {
      return isLocked(this.scheduledAt, this.nowTs);
    },

    entryLockMinutes() {
      return ENTRY_LOCK_MINUTES;
    },

    scheduledAtLabel() {
      if (!this.scheduledAt) return "";
      return new Date(this.scheduledAt).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    unlockAtLabel() {
      const unlock = unlockAt(this.scheduledAt);
      if (unlock === null) return "";
      return new Date(unlock).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.vm-logo {
  display: block;
  height: 24px;
  width: auto;
}

/* ─── PRECALL SHELL ─── */
.precall {
  background: #0b1220;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ─── TOP BAR ─── */
.precall-top {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  .vm-mark {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    text-decoration: none;
  }

  .vm-wordmark {
    font-size: var(--font-size-md);
    font-weight: var(--fw-extra);
    color: #fff;
    letter-spacing: -0.01em;
  }

  .ttl {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-bold);
    color: rgba(255, 255, 255, 0.85);
  }

  .clock {
    margin-left: auto;
    font-family: var(--font-num, "Inter", sans-serif);
    font-size: var(--font-size-sm);
    font-weight: var(--fw-bold);
    color: rgba(255, 255, 255, 0.6);
  }
}

/* ─── BODY GRID ─── */
.precall-body {
  flex: 1;
  display: grid;
  grid-template-columns: 1.5fr 0.85fr;
  gap: var(--space-8);
  padding: var(--space-10);
  align-items: center;
  max-width: 1120px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    padding: var(--space-5);
  }
}

/* ─── CAM PREVIEW ─── */
.cam-preview {
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-xl);
  background: linear-gradient(160deg, #1e293b, #0f172a);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.selfname-row {
  position: absolute;
  left: var(--space-4);
  bottom: var(--space-4);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: rgba(0, 0, 0, 0.4);
  border-radius: var(--radius-sm);
  padding: 4px 10px;
}

.selfname-txt {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-bold);
  color: #fff;
}

.cam-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius-xl);
  transform: scaleX(-1);
}

.cam-controls {
  position: absolute;
  bottom: var(--space-4);
  right: var(--space-4);
  display: flex;
  gap: var(--space-3);
  align-items: center;
  z-index: 2;
}

.mic-wrapper {
  position: relative;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.sound-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  pointer-events: none;
  will-change: transform, opacity;
  transition: transform 0.06s ease-out, opacity 0.06s ease-out;
}

.mic-wrapper .cam-ctrl {
  position: relative;
  z-index: 1;
  cursor: pointer;
}

.settings-panel {
  position: absolute;
  bottom: 80px;
  right: var(--space-4);
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 220px;
  z-index: 3;
}

.settings-title {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-bold);
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 var(--space-1);
}

.settings-label {
  font-size: var(--font-size-xs);
  color: rgba(255, 255, 255, 0.7);
  margin-top: var(--space-2);
}

.settings-select {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-md);
  color: #fff;
  font-size: var(--font-size-xs);
  padding: var(--space-2) var(--space-3);
  width: 100%;
  outline: none;

  option {
    background: #1e293b;
  }
}

.cam-ctrl {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
  }
  &.off {
    background: var(--color-danger);
  }
}

/* ─── READY PANEL ─── */
.ready-panel {
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);

  h2 {
    font-size: var(--font-size-2xl);
    font-weight: var(--fw-extra);
    color: #fff;
    letter-spacing: -0.01em;
    margin: 0 0 var(--space-2);
  }

  p {
    font-size: var(--font-size-sm);
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
    line-height: 1.55;
  }
}

.ready-doc {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
}

.ready-checks {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);

  .it {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    font-size: var(--font-size-sm);
    color: rgba(255, 255, 255, 0.8);
  }

  .ck {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(22, 163, 74, 0.2);
    color: var(--color-success);
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;

    &.ck--fail {
      background: rgba(220, 38, 38, 0.2);
      color: var(--color-danger);
    }
  }
}

.lockbox {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.55);

  &.lockbox--unlocked {
    border-color: rgba(22, 163, 74, 0.35);
    background: rgba(22, 163, 74, 0.1);
    color: #4ade80;
  }
}

.lock-strong {
  font-size: 13px;
  font-weight: var(--fw-bold);
  color: #fff;
}

.lock-weak {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

.copy-link-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.2);

  &:hover {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.5);
  }
}
</style>
